import {
  convertFromRaw,
  convertToRaw,
  type DraftInlineStyleType,
  Editor,
  EditorState,
  RichUtils,
} from "draft-js";
import "draft-js/dist/Draft.css";
import { useTranslation } from "react-i18next";
import { useFormResponseStore } from "@/store";
import React, { useEffect, useState } from "react";

interface ParagraphProps {
  question: any;
  sectionId: string;
  sectionName: string;
  setValidationErrors: (
    sectionId: string,
    questionId: string,
    error: string | null
  ) => void;
}

export const ParagraphAnswer: React.FC<ParagraphProps> = ({
  question,
  sectionId,
  sectionName,
  setValidationErrors,
}) => {
  const { t } = useTranslation();
  const { setResponse, formResponses } = useFormResponseStore();

  const existingSection = formResponses[sectionId];
  const existingResponse = existingSection?.questions[question.id]?.response || "";
  
  // Helper function to convert plain text to Draft.js format
  const convertTextToDraftJsFormat = (text: string) => {
    return {
      blocks: [
        {
          key: "initial",
          text: text,
          type: "unstyled",
          depth: 0,
          inlineStyleRanges: [],
          entityRanges: [],
          data: {}
        }
      ],
      entityMap: {}
    };
  };

  // Safely parse JSON response
  const getInitialEditorState = () => {
    if (!existingResponse) {
      return EditorState.createEmpty();
    }
    
    // Handle different types of existing response
    let parsedResponse;
    
    if (typeof existingResponse === 'string') {
      const trimmedResponse = existingResponse.trim();
      
      // Check if the string looks like it could be valid JSON
      if (trimmedResponse.length >= 3 && (trimmedResponse.startsWith('{') || trimmedResponse.startsWith('['))) {
        try {
          parsedResponse = JSON.parse(existingResponse);
          // Validate that the parsed response has the expected structure for Draft.js
          if (!parsedResponse || typeof parsedResponse !== 'object' || !parsedResponse.blocks) {
            parsedResponse = convertTextToDraftJsFormat(existingResponse);
          }
        } catch (error) {
          parsedResponse = convertTextToDraftJsFormat(existingResponse);
        }
      } else {
        // It's plain text, convert to Draft.js format
        parsedResponse = convertTextToDraftJsFormat(existingResponse);
      }
    } else if (typeof existingResponse === 'object' && existingResponse !== null) {
      // If it's already an object, validate it has the expected structure
      if (existingResponse.blocks && Array.isArray(existingResponse.blocks)) {
        parsedResponse = existingResponse;
      } else {
        parsedResponse = convertTextToDraftJsFormat(JSON.stringify(existingResponse));
      }
    } else {
      // For other types (number, boolean, etc.), convert to text and then to Draft.js format
      parsedResponse = convertTextToDraftJsFormat(String(existingResponse));
    }
    
    try {
      return EditorState.createWithContent(convertFromRaw(parsedResponse));
    } catch (error) {
      return EditorState.createEmpty();
    }
  };
  
  const initialEditorState = getInitialEditorState();

  const [editorState, setEditorState] = useState(initialEditorState);

  useEffect(() => {
    const content = editorState.getCurrentContent();
    const inputValue = JSON.stringify(convertToRaw(content));

    setResponse(
      sectionId,
      sectionName,
      question.id,
      question.type,
      question.label,
      inputValue
    );

    if (question.required == "yes" && !inputValue) {
      setValidationErrors(
        sectionId,
        question.id,
        `${question.label} ${t('forms.isRequired')}`
      );
    } else if (question.minCharacters && inputValue.length < question.minCharacters) {
      setValidationErrors(
        sectionId,
        question.id,
        t('forms.mustBeAtLeastCharacters', { field: question.label, count: question.minCharacters })
      );
    } else if (question.maxCharacters && inputValue.length > question.maxCharacters) {
      setValidationErrors(
        sectionId,
        question.id,
        t('forms.mustBeLessThanCharacters', { field: question.label, count: question.maxCharacters })
      );
    } else {
      setValidationErrors(sectionId, question.id, null);
    }
  }, [editorState]); // Update validation errors whenever editorState changes

  const handleEditorChange = (state: EditorState) => {
    setEditorState(state);
  };

  const handleKeyCommand = (command: string, state: EditorState) => {
    const newState = RichUtils.handleKeyCommand(state, command);
    if (newState) {
      handleEditorChange(newState);
      return "handled";
    }
    return "not-handled";
  };

  const toggleInlineStyle = (style: DraftInlineStyleType) => {
    handleEditorChange(RichUtils.toggleInlineStyle(editorState, style));
  };

  const toggleBlockType = (blockType: string) => {
    handleEditorChange(RichUtils.toggleBlockType(editorState, blockType));
  };

  const inlineStyles = [
    { label: t('forms.heading1'), style: "H1" },
    { label: t('forms.heading2'), style: "H2" },
    { label: t('forms.heading3'), style: "H3" },
    { label: t('forms.heading4'), style: "H4" },
    { label: t('forms.heading5'), style: "H5" },
    { label: t('forms.heading6'), style: "H6" },
    { label: t('forms.bold'), style: "BOLD" },
    { label: t('forms.italic'), style: "ITALIC" },
    { label: t('forms.underline'), style: "UNDERLINE" },
  ];

  const blockTypes = [
    { label: t('forms.unorderedList'), style: "unordered-list-item" },
    { label: t('forms.orderedList'), style: "ordered-list-item" },
  ];

  const customStyleMap = {
    H1: { fontSize: "2em" },
    H2: { fontSize: "1.75em" },
    H3: { fontSize: "1.5em" },
    H4: { fontSize: "1.25em" },
    H5: { fontSize: "1.1em" },
    H6: { fontSize: "1em" },
  };

  return (
    <div className="mb-4 ">
      <label className="main-label">
        {question.label}{" "}
        {question.required == "yes" && <span className="text-red-500">*</span>}
      </label>
      <div className="text-editor">
        <div className="toolbar">
          {inlineStyles.map((type) => (
            <button
              key={type.label}
              type="button"
              onClick={() => toggleInlineStyle(type.style as any)}
              className="toolbar-button"
            >
              {type.label}
            </button>
          ))}
          {blockTypes.map((type) => (
            <button
              key={type.label}
              type="button"
              onClick={() => toggleBlockType(type.style)}
              className="toolbar-button"
            >
              {type.label}
            </button>
          ))}
        </div>
        <div className="editor-container">
          {/* @ts-ignore */}
          <Editor
            editorState={editorState}
            handleKeyCommand={handleKeyCommand}
            onChange={handleEditorChange}
            placeholder={t('forms.addDescriptionContent')}
            customStyleMap={customStyleMap}
          />
        </div>
      </div>
    </div>
  );
};
